import { ESubscriptionTier } from '__generated__/globalTypes';
import { PanelMessages } from 'components/minute/controls/ToolSelectionPanel/PanelMessages';
import { ToolSelectionButton } from 'components/minute/controls/ToolSelectionPanel/ToolSelectionButton';
import { useMinuteTools } from 'components/minute/controls/ToolSelectionPanel/useMinuteTools';
import { IconTextButtonBlock } from 'components/minute/view/IconTextButtonBlock';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { motion } from 'framer-motion';
import { ANIMATION_APPEAR, conditionalScaleAnimation } from 'libs/styles/animations';
import { EBorders } from 'libs/styles/styling';
import { EWorldAction } from 'libs/utils/world/worldTypes';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { CtxEdit } from 'session/ctxEdit';
import { CtxModal } from 'session/ctxModal';
import { GlobalContext } from 'session/state/context';
import { SUBSCRIPTION_CONFIG } from 'static/config/subscriptionConfig';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
}

export const ToolSelectionPanel = (props: Props): JSX.Element => {
    const { largeButtons, smallButtons } = useMinuteTools();
    const ctxEdit = useContext(CtxEdit);
    const action = ctxEdit.worldAction;
    const markerCount = ctxEdit.worldMarkers?.length;
    const globalContext = useContext(GlobalContext);
    const currentUser = globalContext.state.loggedUser;
    const currentTier = currentUser?.subscriptionTier ?? ESubscriptionTier.FREE;
    const navigate = useNavigate();
    const ctxModal = useContext(CtxModal);

    function ifAnyAction() {
        return (
            action === EWorldAction.ICON_V2 ||
            action === EWorldAction.VARIANT_ICON_V2 ||
            action === EWorldAction.IMAGE_V2 ||
            action === EWorldAction.TEXT_V2 ||
            action === EWorldAction.DRAW ||
            action === EWorldAction.DIRECTION ||
            action === EWorldAction.LINE_V2 ||
            action === EWorldAction.POLYGON_V2 ||
            action === EWorldAction.POLYGON_V2_WALL ||
            action === EWorldAction.LINE_V2_WALL ||
            action === EWorldAction.UPLOAD ||
            action === EWorldAction.INDOOR_STAND
        );
    }

    function loggedReachMax() {
        return false; //open for now

        if (!SUBSCRIPTION_CONFIG[currentTier].maxMarkers) return false;

        return currentUser && SUBSCRIPTION_CONFIG[currentTier].maxMarkers <= markerCount;
    }

    function unLoggedReachMax() {
        return !currentUser && SUBSCRIPTION_CONFIG[currentTier].maxMarkers <= markerCount;
    }

    return (
        <S_ToolSelectionPanel className={props.className}>
            {ifAnyAction() && (
                <S_MessageArea>
                    <motion.div {...ANIMATION_APPEAR}>
                        <PanelMessages />
                    </motion.div>
                </S_MessageArea>
            )}
            {!loggedReachMax() && !unLoggedReachMax() && (
                <>
                    {largeButtons.map((item) => {
                        return (
                            <motion.div
                                key={item.label}
                                {...conditionalScaleAnimation(!item.selected, 1.1, 0.9)}
                            >
                                <S_ToolSelectionButton
                                    icon={{ iconSet: item.iconSet, iconName: item.icon }}
                                    $isSmall={false}
                                    selected={item.selected}
                                    onClick={item.onClick}
                                    text={item.label}
                                />
                            </motion.div>
                        );
                    })}
                    <S_Grid>
                        {smallButtons.map((item) => {
                            return (
                                <motion.div
                                    key={item.label}
                                    {...conditionalScaleAnimation(!item.selected, 1.1, 0.9)}
                                >
                                    <S_ToolSelectionButton
                                        key={item.label}
                                        $isSmall={true}
                                        selected={item.selected}
                                        onClick={item.onClick}
                                        text={item.label}
                                    />
                                </motion.div>
                            );
                        })}
                    </S_Grid>
                </>
            )}
            {loggedReachMax() && (
                <S_Warning>
                    <S_IconTextButtonBlock
                        iconName="hand-wave"
                        title="Upgrade to continue making an amazing map"
                        subtitle={`You have reached the marker limit`}
                        buttonText={'Upgrade'}
                        buttonAction={() => {
                            ctxModal.setModal({
                                type: EModal.UPGRADE
                            });
                        }}
                    />
                </S_Warning>
            )}
            {unLoggedReachMax() && (
                <S_Warning>
                    <S_IconTextButtonBlock
                        iconName="hand-wave"
                        title="Register to continue making an amazing map"
                        subtitle={`You have reached the marker limit`}
                        buttonText={'Register'}
                        buttonAction={() => {
                            navigate('/register');
                        }}
                    />
                </S_Warning>
            )}
        </S_ToolSelectionPanel>
    );
};

const S_ToolSelectionPanel = styled.div`
    border-radius: 7px 7px 0px 0px;
    background: #efefef;
    box-shadow: -5px 5px 10px 0px rgba(0, 0, 0, 0.1);
    border-top: 1px solid ${EBorders.DARK};
    border-right: 1px solid ${EBorders.DARK};
    border-left: 1px solid ${EBorders.DARK};
    display: flex;
    justify-content: center;
    padding: 10px;
    column-gap: 10px;
    position: relative;
    top: -90px;
`;

const S_ToolSelectionButton = styled(ToolSelectionButton)<{ $isSmall: boolean }>`
    ${(props) => {
        if (props.$isSmall) {
            return css`
                height: 30px;
                width: 85px;
                &:nth-child(4n + 2) {
                    grid-row: 1;
                }
            `;
        }
    }}
`;

const S_Grid = styled.div`
    display: grid;
    grid-auto-flow: column dense; /* column flow with "dense" to fill all the cells */
    grid-template-rows: auto auto; /* 2 rows */
    grid-auto-columns: auto;
    gap: 10px;
`;

const S_MessageArea = styled.div`
    position: absolute;
    width: 100%;
    bottom: 100px;
`;

const S_Warning = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const S_IconTextButtonBlock = styled(IconTextButtonBlock)`
    width: 400px;
`;
